import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import i18n from '@i18next/i18n';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { SelectedModelType } from '../../selection/selection-service.interface';
import Button from '@tencent/dui/lib/components/Button';
import cancelIcon from '@tencent/docs-design-resources/pc/svg/pcapp_close.svg';
import tickIcon from '@tencent/docs-design-resources/icon/desktop/svg/tick_white_outline_light_24.svg';
import trashIcon from '@tencent/docs-design-resources/pc/svg/toolbar_dele.svg';
import restoreIcon from '@tencent/docs-design-resources/icon/desktop/svg/restore_24.svg';
import imageIcon from '@src/js/lzimg/fail-image-icon.svg';
import { ReplaceImageUtils } from '../utils/replace-image-utils';
import { convertImageSize } from '@src/slide/application/service/ai-ssistant/ai-generator/image-utils';
import { generateImage } from '@src/slide/application/service/ai/core/api/api';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import styles from '@src/slide/application/service/smart-replace-image/replace-card/replace-card.less';

// 简易的生图的全局缓存
class GenerateImageStore {
    // modelId -> (page -> imageInfo[])
    public static store: Record<string, Record<number, DisplayImageInfo[]>> = {};
}

interface DisplayImageInfo {
    url?: string;
    fail?: boolean;
}

interface GenerateInfo {
    url?: string;
    needGenerate?: boolean;
    generateDetail?: {
        width: number;
        height: number;
        title: string;
        keyword: string;
    };
}

interface IReplaceGenerateCardProps {
    onClose: () => void;
    selectedModel: SelectedModelType;
    count?: number;
    maxPage?: number;
}

// 重新生成图片弹窗
export const ReplaceGenerateCard = ({ count = 4, maxPage = 3, selectedModel, onClose }: IReplaceGenerateCardProps) => {
    const [message, setMessage] = useState<string>(''); // 卡片的文案提示
    // const [displayImages, setDisplayImages] = useState<DisplayImageInfo[]>([]);
    const [pageIndex, setPageIndex] = useState<number>(0);
    const [selectedImage, setSelectedImage] = useState<{ page: number; index: number }>();
    const [generateInfo, setGenerateInfo] = useState<GenerateInfo[]>([]); // 用于生成图片的信息，传递给图片组件进行图片生成
    const [completed, setCompleted] = useState<'success' | 'loading' | 'fail'>('loading'); // 所有图片是否全部生成完成

    const imagesInfo = useRef<Record<number, DisplayImageInfo[]>>({});
    const titleList = useRef<string[]>([]);

    useEffect(() => {
        const title = SlideDataServices.SlideTitleManager()?.title ?? '';
        const pageTitle = ReplaceImageUtils.getPageTitle(selectedModel?.slideRef ?? undefined) ?? '';
        const subTitle = ReplaceImageUtils.getPageTitleList(selectedModel?.slideRef ?? undefined);
        titleList.current = [title, pageTitle, ...subTitle];
        updateDisplayImages();
    }, [pageIndex]);

    useEffect(() => {
        const message = {
            success: i18n.t('已经为你重新生成了下列图片：'),
            loading: i18n.t('正在重新智能配图，请稍候...'),
            fail: i18n.t('很抱歉，配图生成失败'),
        }[completed];
        setMessage(message);
    }, [completed]);

    const setCompletedStatus = () => {
        const cacheImagesInfo = imagesInfo.current[pageIndex];
        if (cacheImagesInfo.every(i => i.fail)) {
            setCompleted('fail');
        } else {
            setCompleted('success');
        }
    };

    const getSelectedImageInfo = (): DisplayImageInfo | undefined => {
        if (!selectedImage) {
            return undefined;
        }
        const { page, index } = selectedImage;
        return imagesInfo.current[page]?.[index];
    };

    // 追加可选择的图片
    const updateDisplayImages = async () => {
        if (!selectedModel?.id) {
            return;
        }
        // 缓存
        const modelImageData = GenerateImageStore.store[selectedModel.id]?.[pageIndex];
        if (modelImageData?.length > 0) {
            imagesInfo.current[pageIndex] = modelImageData;
            setGenerateInfo(modelImageData.map(i => ({ url: i.url })));
            setCompletedStatus();
            return;
        }

        setCompleted('loading');
        const size = convertImageSize((selectedModel as IPictureModel).props.style);
        const keyword = (selectedModel as IPictureModel).sdkData?.props.nvPr.name ?? '';
        if (!selectedModel?.slideRef) {
            // 没有页数据
            return;
        }
        const generateInfo = Array.from({ length: count }, (item, index) => {
            const title = titleList.current[((pageIndex * count) + index) % titleList.current.length];
            return {
                needGenerate: true,
                generateDetail: {
                    width: size.width,
                    height: size.height,
                    title,
                    keyword,
                },
            };
        });
        setGenerateInfo(generateInfo);
    };

    // 图片生成完成，存入缓存
    const generateEnd = (info: DisplayImageInfo, index) => {
        const { url, fail } = info;
        if (!imagesInfo.current[pageIndex]) {
            imagesInfo.current[pageIndex] = [];
        }
        const cacheImagesInfo = imagesInfo.current[pageIndex];
        cacheImagesInfo[index] = { url, fail };

        if (cacheImagesInfo.map(i => i).filter(Boolean).length >= count) {
            setCompletedStatus();

            if (selectedModel?.id) {
                const data = GenerateImageStore.store[selectedModel.id];
                GenerateImageStore.store[selectedModel.id] = { ...data, [pageIndex]: imagesInfo.current[pageIndex] };
            }
        }
    };

    const replaceImage = () => {
        const image = getSelectedImageInfo();
        if (!image?.url) {
            return;
        }

        const generateDetail = generateInfo[selectedImage?.index ?? 0]?.generateDetail;
        const { width = 0, height = 0 } = generateDetail ?? {};
        ReplaceImageUtils.replaceImagesWithSearchInfo(
            [selectedModel as IPictureModel],
            {
                url: image.url,
                width,
                height,
                title: '',
                source_url: '',
                thumb_url: image.url,
                thumb_width: width,
                thumb_height: height,
            },
            true,
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.message}>{message}</div>
            <div className={styles['image-galley']}>
                {Array.from({ length: count }, (_, index) => (
                    <ImageItem
                        key={`${index}`}
                        generateInfo={generateInfo[index]}
                        checked={pageIndex === selectedImage?.page && index === selectedImage?.index}
                        onClick={() => setSelectedImage({ page: pageIndex, index })}
                        onGenerateEnd={info => generateEnd(info, index)}
                    />
                ))}
            </div>
            <div className={styles.footer}>
                <div className={styles['footer-left']}>
                    {completed === 'loading' ? (
                        <Button
                            size="small"
                            className={styles['ai-button-normal']}
                            icon={cancelIcon}
                            onClick={() => onClose()}
                        >
                            {i18n.t('取消')}
                        </Button>
                    ) : (
                        <>
                            <Button
                                size="small"
                                className={styles['ai-button-normal']}
                                icon={trashIcon}
                                onClick={() => onClose()}
                            >
                                {i18n.t('退出')}
                            </Button>
                            <Button
                                size="small"
                                className={styles['ai-button-normal']}
                                icon={restoreIcon}
                                onClick={() => setPageIndex((pageIndex + 1) % maxPage)}
                            >
                                {i18n.t('换一批')}
                            </Button>
                        </>
                    )}
                </div>
                <div className={styles['footer-right']}>
                    <Button
                        size="small"
                        className={classNames({
                            [styles['ai-button-primary']]: true,
                            [styles['ai-button-disabled']]:
                                !getSelectedImageInfo()?.url || selectedImage?.page !== pageIndex,
                        })}
                        icon={tickIcon}
                        onClick={() => {
                            replaceImage();
                            onClose();
                        }}
                    >
                        {i18n.t('替换图片')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

interface IImageItemProps {
    generateInfo?: GenerateInfo;
    checked?: boolean;
    onClick?: () => void;
    onGenerateEnd?: (info: DisplayImageInfo) => void;
}
const ImageItem = ({ generateInfo, checked, onClick, onGenerateEnd }: IImageItemProps) => {
    const [url, setUrl] = useState<string>(); // 当前显示的图片链接
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!generateInfo) {
            return;
        }
        const { url, needGenerate, generateDetail } = generateInfo;
        if (!needGenerate || !generateDetail) {
            setUrl(url || imageIcon);
            return;
        }
        const { keyword, width, height, title } = generateDetail;
        setLoading(true);
        setUrl(undefined);
        generateImage({
            keyword,
            width,
            height,
            title,
            upload: false,
        }).then((result) => {
            if (result?.generate_image.url) {
                const url = result?.generate_image.url;
                setUrl(url);
                setLoading(false);
                onGenerateEnd?.({ url });
            } else {
                setUrl(imageIcon);
                setLoading(false);
                onGenerateEnd?.({ fail: true });
            }
        });
    }, [generateInfo]);

    const getImageStyle = () => {
        if (url) {
            return { backgroundImage: `url(${url})` };
        }
    };

    return (
        <div
            className={classNames({
                [styles['card-image']]: true,
                [styles['card-image-loading']]: loading,
                [styles['card-image-active']]: checked,
            })}
            onClick={() => onClick?.()}
            style={getImageStyle()}
        ></div>
    );
};
